import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Paper,
  useMediaQuery,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  CircularProgress,
  Alert,
  Collapse,
  Tooltip,
} from '@mui/material'

import Snackbar from '@mui/material/Snackbar'
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as ShoppingCartIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { Link } from 'react-router-dom'
import api from '../../config/api'
import { API_URL, API_WITH_OUT_PROTOCOL } from '../../config/config'

import ErrorSnackbar from '../ErrorSnackbar/ErrorSnackbar'
import { DeliveryAddressesSelect } from '../User/DeliverySelect/DeliverySelect'
import ThankYouComponent from '../ThankYouComponent/ThankYouComponent'

const Cart = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [cartItems, setCartItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [warning, setWarning] = useState(null)
  const [missingProducts, setMissingProducts] = useState([])
  const [localQuantities, setLocalQuantities] = useState({})
  const [initialTtl, setInitialTtl] = useState(null) // Начальное значение TTL
  const [currentTtl, setCurrentTtl] = useState(null) // Текущее значение (считаем на клиенте)
  const [lastUpdated, setLastUpdated] = useState(null) // Время последнего обновления
  const updateTimeoutRef = useRef(null)
  const ttlIntervalRef = useRef(null)
  const [selectedAddress, setSelectedAddress] = useState('')
  const [comment, setComment] = useState('')
  const socketRef = useRef(null)
  const [errorSubmite, setErrorSubmite] = useState('')
  const [successCreation, setSuccessCreation] = useState(false)

  useEffect(() => {
    const connectWebSocket = () => {
      const token = localStorage.getItem('access_token')
      socketRef.current = new WebSocket(
        `ws://${API_WITH_OUT_PROTOCOL}/ws/stock/?token=${token}`,
      )

      socketRef.current.onopen = () => {
        console.log('WebSocket подключен')
      }

      socketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('Полученные данные:', data)

          if (data.product_id && typeof data.quantity === 'number') {
            updateStock(data)
          } else {
            console.warn('Некорректные данные WebSocket:', data)
          }
        } catch (error) {
          console.error('Ошибка парсинга WebSocket данных:', error)
        }
      }

      socketRef.current.onerror = (error) => {
        console.error('WebSocket ошибка:', error)
      }

      socketRef.current.onclose = () => {
        console.log('WebSocket отключен, переподключение...')
        setTimeout(connectWebSocket, 3000)
      }
    }

    connectWebSocket()

    return () => {
      socketRef.current?.close()
    }
  }, [])

  const updateStock = (data) => {
    setCartItems((prevProducts) => {
      const updatedProducts = prevProducts.map((product) =>
        product.product_id === data.product_id
          ? { ...product, available_count: data.quantity }
          : product,
      )
      return [...updatedProducts] // Создаем новый массив для ререндера
    })
  }

  useEffect(() => {
    console.log(cartItems)
  }, [cartItems])
  // Форматирование времени
  const formatTime = (seconds) => {
    if (seconds === -1) return '∞'
    if (seconds === -2) return 'Ошибка получения времени'
    if (seconds <= 0) return 'Скоро будет удалена'

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    return [
      hours > 0 ? `${hours}ч` : null,
      minutes > 0 ? `${minutes}м` : null,
      `${secs}с`,
    ]
      .filter(Boolean)
      .join(' ')
  }

  // Получение цвета для TTL
  const getTtlColor = (seconds) => {
    if (seconds === -1) return '#4caf50' // Бесконечное - зеленый
    if (seconds === -2) return '#f44336' // Ошибка - красный
    if (seconds <= 60) return '#ff5722' // Меньше минуты - оранжевый
    if (seconds <= 300) return '#ff9800' // Меньше 5 минут - желто-оранжевый
    if (seconds <= 150) return '#ba000d' // Меньше 2.5 минут - желто-оранжевый

    // Градиент для остальных случаев
    const maxTtl = 24 * 3600 // 24 часа
    const ratio = Math.min(seconds / maxTtl, 1)

    const r = Math.floor(76 + (244 - 76) * (1 - ratio))
    const g = Math.floor(175 + (67 - 175) * (1 - ratio))
    const b = Math.floor(80 + (54 - 80) * (1 - ratio))

    return `rgb(${r}, ${g}, ${b})`
  }

  // Загрузка корзины и начального TTL
  useEffect(() => {
    const fetchCartAndInitialTtl = async () => {
      try {
        setLoading(true)
        setError(null)
        setWarning(null)
        setMissingProducts([])

        // Загружаем корзину
        const cartResponse = await api.get('/API_FRONT/cart/')

        if (cartResponse.status === 206) {
          setCartItems(cartResponse.data.cart_items || [])
          setWarning(cartResponse.data.warning)
          setMissingProducts(cartResponse.data.missing_products || [])
        } else if (cartResponse.status === 200) {
          setCartItems(cartResponse.data || [])
        } else if (cartResponse.status === 404) {
          setWarning('Некоторые товары недоступны')
          setMissingProducts(cartResponse.data.missing_products || [])
          setCartItems([])
        }

        // Загружаем начальный TTL один раз
        const ttlResponse = await api.get('/API_FRONT/cart/ttl/')
        setInitialTtl(ttlResponse.data.time)
        setCurrentTtl(ttlResponse.data.time)
        setLastUpdated(Date.now())
      } catch (err) {
        if (err.response) {
          if (err.response.status === 404) {
            setError('Корзина пуста или недоступна')
          } else {
            setError(`Ошибка загрузки: ${err.response.statusText}`)
          }
        } else {
          setError('Ошибка соединения')
        }
        console.error('Ошибка загрузки корзины:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchCartAndInitialTtl()

    return () => {
      if (ttlIntervalRef.current) clearInterval(ttlIntervalRef.current)
      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current)
    }
  }, [])

  // Клиентский отсчет времени
  useEffect(() => {
    if (initialTtl === null || initialTtl <= 0) return

    ttlIntervalRef.current = setInterval(() => {
      setCurrentTtl((prev) => {
        if (prev === -1 || prev === -2 || prev <= 0) return prev
        return prev - 1
      })
    }, 1000)

    return () => {
      if (ttlIntervalRef.current) clearInterval(ttlIntervalRef.current)
    }
  }, [initialTtl])

  // Обновление TTL при изменении корзины
  useEffect(() => {
    if (cartItems.length === 0) {
      setCurrentTtl(-2)
      if (ttlIntervalRef.current) clearInterval(ttlIntervalRef.current)
    }
  }, [cartItems])

  // Обновление количества товара с учетом шага (после завершения ввода)
  const updateQuantity = async (productId, newQuantity, stack = 1) => {
    // Очищаем предыдущий таймер
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current)
    }

    // Округляем до ближайшего значения, кратного шагу
    const roundedQuantity = Math.round(newQuantity / stack) * stack
    const finalQuantity = Math.max(roundedQuantity, stack) // Не меньше шага

    try {
      await api.put('/API_FRONT/cart/update/', {
        product_id: productId,
        quantity: finalQuantity,
      })

      setCartItems(
        cartItems.map((item) =>
          item.product_id === productId
            ? { ...item, quantity: finalQuantity }
            : item,
        ),
      )

      // Очищаем локальное значение после успешного обновления
      setLocalQuantities((prev) => {
        const newValues = { ...prev }
        delete newValues[productId]
        return newValues
      })
    } catch (err) {
      console.error('Ошибка обновления:', err)
      setErrorSubmite('Ошибка обновления кол-ва товара, попробуйте позже')
    }
  }

  // Обработчик изменения значения вручную
  const handleManualQuantityChange = (productId, value, stack) => {
    const numericValue = parseInt(value) || stack

    // Сохраняем в локальное состояние
    setLocalQuantities((prev) => ({
      ...prev,
      [productId]: numericValue,
    }))

    // Устанавливаем таймер для обновления через 1 секунду после последнего ввода
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current)
    }

    updateTimeoutRef.current = setTimeout(() => {
      updateQuantity(productId, numericValue, stack)
    }, 1000)
  }

  // Обработчик потери фокуса
  const handleBlur = (productId, stack) => {
    if (localQuantities[productId] !== undefined) {
      updateQuantity(productId, localQuantities[productId], stack)
    }
  }

  // Удаление товара
  const handleRemoveItem = async (productId) => {
    try {
      await api.delete('/API_FRONT/cart/remove/', {
        data: { product_id: productId },
      })
      setCartItems(cartItems.filter((item) => item.product_id !== productId))
      setMissingProducts(missingProducts.filter((id) => id !== productId))
    } catch (err) {
      console.error('Ошибка удаления:', err)
    }
  }

  // Оформление заказа
  const handleCheckout = async () => {
    try {
      setLoading(true)
      let response = await api.post('/API_FRONT/cart/save/', {
        comment,
        selectedAddress,
      })
      if (response.status === 200) {
        setSuccessCreation(true)
      }
    } catch (err) {
      console.error('Ошибка оформления:', err)
      setErrorSubmite('Ошибка при отправке запроса.Попробуйте позже')
    }
  }

  // Расчет общей суммы
  const total = cartItems.reduce((sum, item) => {
    return sum + (item.price || 0) * item.quantity
  }, 0)

  // Очищаем таймер при размонтировании
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }
    }
  }, [])

  // if (loading) {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
  //       <CircularProgress />
  //     </Box>
  //   )
  // }

  // if (error) {
  //   return (
  //     <Box sx={{ textAlign: 'center', p: 3 }}>
  //       <Typography color="error">{error}</Typography>
  //       <Button
  //         onClick={() => window.location.reload()}
  //         variant="outlined"
  //         sx={{ mt: 2 }}
  //       >
  //         Повторить попытку
  //       </Button>
  //     </Box>
  //   )
  // }

  if (successCreation) {
    return <ThankYouComponent />
  }

  return (
    <Paper elevation={isMobile ? 0 : 1} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Badge badgeContent={cartItems.length} color="primary">
          <ShoppingCartIcon color="action" />
        </Badge>
        <Typography variant="h6" sx={{ ml: 1 }}>
          Ваша корзина
        </Typography>
      </Box>

      {/* Блок с TTL таймером */}
      {currentTtl !== null && cartItems.length !== 0 && (
        <Tooltip
          title={
            currentTtl === -1
              ? 'Ваша корзина хранится бессрочно'
              : currentTtl === -2
                ? 'Ошибка получения оставшегося времени коризны'
                : 'Оставшееся время до автоматического удаления корзины'
          }
          arrow
        >
          <Box
            sx={{
              ml: isMobile ? 0 : 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: theme.palette.background.default,
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
            }}
          >
            <AccessTimeIcon
              fontSize="small"
              sx={{
                color: getTtlColor(currentTtl),
                opacity: 0.8,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: getTtlColor(currentTtl),
                whiteSpace: 'nowrap',
                fontFamily: 'monospace',
              }}
            >
              {currentTtl === -1
                ? 'Бессрочно'
                : currentTtl === -2
                  ? 'Ошибка получения оставшегося времени коризны'
                  : `Удаление: ${formatTime(currentTtl)}`}
            </Typography>
          </Box>
        </Tooltip>
      )}

      <Collapse in={!!warning}>
        <Alert
          severity="warning"
          action={
            <IconButton size="small" onClick={() => setWarning(null)}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mb: 2 }}
        >
          {warning}
          {missingProducts.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Недоступные товары: {missingProducts.join(', ')}
            </Typography>
          )}
        </Alert>
      </Collapse>

      {cartItems.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Корзина пуста
          </Typography>
          <Button component={Link} to="/" variant="contained" sx={{ mt: 2 }}>
            Перейти в каталог
          </Button>
        </Box>
      ) : (
        <>
          <List dense={isMobile}>
            {cartItems.map((item) => {
              const stack = item.category?.stack || 1
              const minQuantity = stack
              const maxQuantity =
                Math.floor(item.available_count / stack) * stack
              const displayValue =
                localQuantities[item.product_id] !== undefined
                  ? localQuantities[item.product_id]
                  : item.quantity

              return (
                <React.Fragment key={item.product_id}>
                  <ListItem
                    sx={{
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: 'flex-start',
                      px: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        width: '100%',
                        flexDirection: isMobile ? 'row' : 'row',
                        alignItems: 'center',
                      }}
                    >
                      {/* Изображение товара */}
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src={`${API_URL}${item.image}`}
                          alt={item.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: theme.shape.borderRadius,
                          }}
                        />
                      </Box>

                      {/* Информация о товаре */}
                      <Box
                        sx={{
                          flexGrow: 1,
                          minWidth: 0,
                          mr: isMobile ? 0 : 2,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.name || `Товар #${item.product_id}`}
                        </Typography>

                        <Typography variant="body2" color="text.primary">
                          {item.price?.toLocaleString('ru-RU')} ₽
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          Размеры: {item.size_1}, {item.size_2}, {item.size_3}
                        </Typography>

                        <Typography
                          variant="caption"
                          display="block"
                          color={
                            item.quantity > item.available_count
                              ? 'error'
                              : 'text.secondary'
                          }
                        >
                          Доступно: {item.available_count} шт. (шаг: {stack})
                        </Typography>
                      </Box>
                    </Box>

                    {/* Управление количеством */}
                    <Box
                      sx={{
                        width: '100%',
                        mt: isMobile ? 2 : 0,
                        pl: isMobile ? 9 : 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: isMobile
                            ? 'space-between'
                            : 'flex-end',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              updateQuantity(
                                item.product_id,
                                item.quantity - stack,
                                stack,
                              )
                            }
                            disabled={item.quantity <= minQuantity || loading}
                          >
                            <RemoveIcon
                              fontSize={isMobile ? 'small' : 'medium'}
                            />
                          </IconButton>

                          <TextField
                            value={displayValue}
                            disabled={loading}
                            onChange={(e) =>
                              handleManualQuantityChange(
                                item.product_id,
                                e.target.value,
                                stack,
                              )
                            }
                            onBlur={() => handleBlur(item.product_id, stack)}
                            type="number"
                            inputProps={{
                              min: minQuantity,
                              max: maxQuantity,
                              step: stack,
                              style: {
                                textAlign: 'center',
                                width: isMobile ? '50px' : '60px',
                                padding: isMobile ? '8px 4px' : '8px',
                              },
                            }}
                            variant="outlined"
                            size="small"
                            sx={{ mx: 0.5 }}
                            error={item.quantity > item.available_count}
                          />

                          <IconButton
                            size="small"
                            onClick={() =>
                              updateQuantity(
                                item.product_id,
                                item.quantity + stack,
                                stack,
                              )
                            }
                            disabled={item.quantity >= maxQuantity || loading}
                          >
                            <AddIcon fontSize={isMobile ? 'small' : 'medium'} />
                          </IconButton>
                        </Box>

                        <IconButton
                          edge="end"
                          disabled={loading}
                          onClick={() => handleRemoveItem(item.product_id)}
                          sx={{ ml: isMobile ? 0 : 1 }}
                        >
                          <DeleteIcon
                            fontSize={isMobile ? 'small' : 'medium'}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  </ListItem>
                  <Divider component="li" sx={{ my: 1 }} />
                </React.Fragment>
              )
            })}
          </List>

          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 2,
            }}
          >
            {/* Выбор адреса доставки */}
            <Box sx={{ mb: 3, textAlign: 'left' }}>
              <DeliveryAddressesSelect
                selectedAddress={selectedAddress}
                setSelectedAddress={setSelectedAddress}
              />
            </Box>

            {/* Поле для ввода комментария */}
            <Box sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body1" gutterBottom>
                Комментарий к заказу
              </Typography>
              <TextField
                label="Добавьте комментарий"
                variant="outlined"
                fullWidth
                multiline
                disabled={loading}
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </Box>

            {/* Итого */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0',
                borderTop: '1px solid #e0e0e0',
                borderBottom: '1px solid #e0e0e0',
                mb: 3,
              }}
            >
              <Typography variant="body1">Итого:</Typography>
              <Typography variant="h6" fontWeight="bold">
                {total.toLocaleString('ru-RU')} ₽
              </Typography>
            </Box>

            {/* Кнопки */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <Button component={Link} to="/" variant="outlined" fullWidth>
                Продолжить покупки
              </Button>

              <Button
                variant="contained"
                fullWidth
                onClick={handleCheckout}
                disabled={
                  cartItems.length === 0 ||
                  cartItems.some(
                    (item) => item.quantity > item.available_count,
                  ) ||
                  loading
                }
              >
                Оформить заказ
              </Button>
            </Box>
          </Box>
        </>
      )}

      <Snackbar
        open={errorSubmite != ''}
        autoHideDuration={3000}
        onClose={() => {
          setErrorSubmite('')
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => {
            setErrorSubmite('')
          }}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorSubmite || 'Произошла ошибка!'}
        </Alert>
      </Snackbar>
    </Paper>
  )
}

export default Cart
